// Lightweight runtime performance aggregator (PERFORMANCE_PLAN.md — measurement).
//
// The API client already times every request; this collects those timings (plus
// view-switch and browser boot/chunk timings) into a reviewable summary so we can
// answer "is it actually faster now?" from real usage instead of guessing.
//
// Reviewable two ways:
//   • In-app: the Debug panel's "Perf" tab renders the table below.
//   • Devtools console: `__perf()` prints console.table(perfSummary()) + boot timings.
//
// Pure in-memory and cheap — a ring of recent samples per label. Nothing is sent
// anywhere; medical data never touches these labels (they are method+path / view name).

export interface PerfStat {
  label: string;
  count: number;
  avgMs: number;
  p95Ms: number;
  maxMs: number;
  lastMs: number;
}

const MAX_SAMPLES = 200; // per label (ring)
const samples = new Map<string, number[]>();
const listeners = new Set<() => void>();

function emit() {
  for (const fn of listeners) fn();
}

/** Record one timing sample under a stable label (e.g. "API GET /events", "view:charts"). */
export function recordTiming(label: string, ms: number): void {
  let arr = samples.get(label);
  if (!arr) {
    arr = [];
    samples.set(label, arr);
  }
  arr.push(ms);
  if (arr.length > MAX_SAMPLES) arr.shift();
  emit();
}

function percentile(sorted: number[], p: number): number {
  if (!sorted.length) return 0;
  const idx = Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length));
  return sorted[idx];
}

/** Aggregated stats per label, slowest average first. */
export function perfSummary(): PerfStat[] {
  const out: PerfStat[] = [];
  for (const [label, arr] of samples) {
    if (!arr.length) continue;
    const sorted = arr.slice().sort((a, b) => a - b);
    const sum = arr.reduce((s, n) => s + n, 0);
    out.push({
      label,
      count: arr.length,
      avgMs: Math.round(sum / arr.length),
      p95Ms: Math.round(percentile(sorted, 95)),
      maxMs: Math.round(sorted[sorted.length - 1]),
      lastMs: Math.round(arr[arr.length - 1]),
    });
  }
  return out.sort((a, b) => b.avgMs - a.avgMs);
}

export function clearPerf(): void {
  samples.clear();
  emit();
}

export function subscribePerf(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// ── Browser boot + chunk timings (read on demand from the Performance API) ──────

export interface JsChunkTiming {
  name: string;
  ms: number;
  kb: number;
}

export interface BootTimings {
  /** Time to first byte of the document. */
  ttfbMs?: number;
  /** DOMContentLoaded relative to navigation start. */
  domContentLoadedMs?: number;
  /** Load event relative to navigation start. */
  loadMs?: number;
  /** Per-JS-chunk fetch durations + transfer sizes (initial + lazy route chunks). */
  jsChunks: JsChunkTiming[];
}

export function bootTimings(): BootTimings {
  const result: BootTimings = { jsChunks: [] };
  if (typeof performance === "undefined" || !performance.getEntriesByType) return result;

  const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
  if (nav) {
    result.ttfbMs = Math.round(nav.responseStart);
    result.domContentLoadedMs = Math.round(nav.domContentLoadedEventEnd);
    result.loadMs = Math.round(nav.loadEventEnd);
  }

  for (const e of performance.getEntriesByType("resource") as PerformanceResourceTiming[]) {
    if (e.initiatorType !== "script" && !e.name.endsWith(".js")) continue;
    if (!e.name.endsWith(".js")) continue;
    result.jsChunks.push({
      name: e.name.split("/").pop() ?? e.name,
      ms: Math.round(e.duration),
      kb: Math.round((e.encodedBodySize || e.transferSize || 0) / 1024),
    });
  }
  result.jsChunks.sort((a, b) => b.kb - a.kb);
  return result;
}

/** Exposes `window.__perf()` for ad-hoc review from the devtools console. */
export function installPerfConsole(): void {
  if (typeof window === "undefined") return;
  (window as unknown as { __perf?: () => void }).__perf = () => {
    // eslint-disable-next-line no-console
    console.table(perfSummary());
    // eslint-disable-next-line no-console
    console.log("boot/chunk timings:", bootTimings());
  };
}
